import { MODULES } from '@erp-boilerplate/shared';
import type { ModuleKey } from '@erp-boilerplate/types';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { RouteObject } from 'react-router-dom';

import { ModuleGuard, PermissionGuard } from '@/app/guards';

/**
 * Module routes, generated from the shared registry.
 *
 * Every module gets its path, its permission guard and a page component looked up
 * in `MODULE_PAGES`. Two consequences are the point of doing it this way: a module
 * can never be mounted without its guard, and adding one means a registry entry
 * plus a line in the map below — never a change to the router itself.
 *
 * The trailing `/*` on each path lets a module own its nested detail and edit
 * routes in its own route file, rather than growing this one to a hundred entries.
 */

type ModulePage = LazyExoticComponent<ComponentType>;

const ModulePlaceholder = lazy(() => import('@/pages/ModulePlaceholder'));

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const UsersListPage = lazy(() => import('@/pages/users/UsersListPage'));
const RolesListPage = lazy(() => import('@/pages/roles/RolesListPage'));
const AuditLogsPage = lazy(() => import('@/pages/audit-logs/AuditLogsPage'));
const CustomersModule = lazy(() => import('@/pages/customers/CustomersModule'));
const LeadsModule = lazy(() => import('@/pages/leads/LeadsModule'));
const AppointmentsListPage = lazy(() => import('@/pages/appointments/AppointmentsListPage'));
const LeadSourcesPage = lazy(() => import('@/pages/lead-sources/LeadSourcesPage'));
const ProductsModule = lazy(() => import('@/pages/products/ProductsModule'));
const ProductItemsModule = lazy(() => import('@/pages/product-items/ProductItemsModule'));
const CategoriesPage = lazy(() => import('@/pages/categories/CategoriesPage'));
const LocationsPage = lazy(() => import('@/pages/locations/LocationsPage'));
const QuotationsModule = lazy(() => import('@/pages/quotations/QuotationsModule'));
const SalesModule = lazy(() => import('@/pages/sales/SalesModule'));
const InvoicesListPage = lazy(() => import('@/pages/invoices/InvoicesListPage'));
const PaymentsModule = lazy(() => import('@/pages/payments/PaymentsModule'));
const RentalsModule = lazy(() => import('@/pages/rental-bookings/RentalsModule'));
const BuybacksModule = lazy(() => import('@/pages/buybacks/BuybacksModule'));
const NotificationsModule = lazy(() => import('@/pages/notifications/NotificationsModule'));
const AccountingModule = lazy(() => import('@/pages/accounting/AccountingModule'));
// Phase 11 — Vendors, Procurement & Accounts Payable
const VendorsModule = lazy(() => import('@/pages/vendors/VendorsModule'));
const PurchasesModule = lazy(() => import('@/pages/purchases/PurchasesModule'));
const PayablesModule = lazy(() => import('@/pages/payables/PayablesModule'));
const PurchaseReturnsModule = lazy(() => import('@/pages/purchase-returns/PurchaseReturnsModule'));
const CompanySettingsPage = lazy(() => import('@/pages/company-settings/CompanySettingsPage'));
const IntegrationsPage = lazy(() => import('@/pages/integrations/IntegrationsPage'));
const InboxPage = lazy(() => import('@/pages/inbox/InboxPage'));

/**
 * Implemented module pages. Anything absent falls back to `ModulePlaceholder`,
 * which states plainly that the module is not built yet rather than rendering an
 * empty screen that reads as broken.
 */
const MODULE_PAGES: Partial<Record<ModuleKey, ModulePage>> = {
  dashboard: DashboardPage,
  users: UsersListPage,
  roles: RolesListPage,
  audit_logs: AuditLogsPage,
  // Phase 2A — Customer + CRM
  customers: CustomersModule,
  leads: LeadsModule,
  appointments: AppointmentsListPage,
  lead_sources: LeadSourcesPage,
  // Phase 2B — Product & Physical Inventory
  products: ProductsModule,
  product_items: ProductItemsModule,
  categories: CategoriesPage,
  inventory_locations: LocationsPage,
  // Phase 3A — Quotation, Sale & Invoice
  quotations: QuotationsModule,
  sales: SalesModule,
  invoices: InvoicesListPage,
  // Phase 3B — Payments, Receipts & Outstanding
  payments: PaymentsModule,
  // Phase 4A — Rental Availability & Booking Foundation
  rentals: RentalsModule,
  // Phase 5 — Buyback / Purchase-Back
  buybacks: BuybacksModule,
  // Phase 7 — Notifications & Customer Communication
  notifications: NotificationsModule,
  // Phase 8 — Finance & Accounting Foundation
  accounting: AccountingModule,
  // Phase 11 — Vendors, Procurement & Accounts Payable
  vendors: VendorsModule,
  purchases: PurchasesModule,
  payables: PayablesModule,
  purchase_returns: PurchaseReturnsModule,
  // Company settings — business profile and document printing
  company_settings: CompanySettingsPage,
  // Lead capture & social inbox
  integrations: IntegrationsPage,
  inbox: InboxPage,
};

const RentalAgreementsListPage = lazy(
  () => import('@/pages/rental-fulfilment/RentalAgreementsListPage'),
);
const RentalAgreementDetailPage = lazy(
  () => import('@/pages/rental-fulfilment/RentalAgreementDetailPage'),
);
const RentalAgreementPrintPage = lazy(
  () => import('@/pages/rental-fulfilment/RentalAgreementPrintPage'),
);
const RentalDispatchesListPage = lazy(
  () => import('@/pages/rental-fulfilment/RentalDispatchesListPage'),
);
const RentalDispatchForm = lazy(() => import('@/pages/rental-fulfilment/RentalDispatchForm'));
const RentalDispatchDetailPage = lazy(
  () => import('@/pages/rental-fulfilment/RentalDispatchDetailPage'),
);
const RentalDispatchPrintPage = lazy(
  () => import('@/pages/rental-fulfilment/RentalDispatchPrintPage'),
);
const RentalReturnsListPage = lazy(() => import('@/pages/rental-returns/RentalReturnsListPage'));
const RentalReturnForm = lazy(() => import('@/pages/rental-returns/RentalReturnForm'));
const RentalReturnDetailPage = lazy(() => import('@/pages/rental-returns/RentalReturnDetailPage'));
const RentalReturnPrintPage = lazy(() => import('@/pages/rental-returns/RentalReturnPrintPage'));
const RentalCalendarPage = lazy(() => import('@/pages/rental-reports/RentalCalendarPage'));
const RentalOverduePage = lazy(() => import('@/pages/rental-reports/RentalOverduePage'));
const RentalReportsPage = lazy(() => import('@/pages/rental-reports/RentalReportsPage'));

/**
 * Routes that are not their own module (Phases 4B and 4C).
 *
 * Rental agreements, dispatch and returns are stages of the *rentals* document,
 * not separate modules: they share the `rentals:*` permission namespace and one
 * sidebar entry, and giving each its own `ModuleKey` would fragment RBAC into
 * four permission sets for one business flow. So they are guarded directly by
 * `PermissionGuard` with the rental permission and reached from the tab strip on
 * the rental screens.
 *
 * Registered ahead of the generated module routes in the router, for the same
 * reason the account screens are: a module splat must not swallow them.
 */
export const rentalFulfilmentRoutes: RouteObject[] = [
  {
    path: 'rental-agreements',
    element: <PermissionGuard permission="rentals:view" />,
    children: [
      { index: true, element: <RentalAgreementsListPage /> },
      { path: ':id', element: <RentalAgreementDetailPage /> },
      { path: ':id/print', element: <RentalAgreementPrintPage /> },
    ],
  },
  {
    path: 'rental-dispatches',
    element: <PermissionGuard permission="rentals:view" />,
    children: [
      { index: true, element: <RentalDispatchesListPage /> },
      { path: 'new', element: <RentalDispatchForm /> },
      { path: ':id', element: <RentalDispatchDetailPage /> },
      { path: ':id/edit', element: <RentalDispatchForm /> },
      { path: ':id/print', element: <RentalDispatchPrintPage /> },
    ],
  },
  {
    path: 'rental-returns',
    element: <PermissionGuard permission="rentals:view" />,
    children: [
      { index: true, element: <RentalReturnsListPage /> },
      // `new` carries the agreement in the query string; `:id/inspect` is the
      // same screen re-entered to correct a draft, because taking the pieces
      // back and grading them is one job, not two.
      { path: 'new', element: <RentalReturnForm /> },
      { path: ':id', element: <RentalReturnDetailPage /> },
      { path: ':id/inspect', element: <RentalReturnForm /> },
      { path: ':id/print', element: <RentalReturnPrintPage /> },
    ],
  },
  // Phase 4D — views across the whole rental flow rather than steps in it.
  // Read-only, and guarded by the same `rentals:view` as the pipeline screens;
  // the finance reports gate themselves further on `rentals:export`.
  {
    path: 'rental-calendar',
    element: <PermissionGuard permission="rentals:view" />,
    children: [{ index: true, element: <RentalCalendarPage /> }],
  },
  {
    path: 'rental-overdue',
    element: <PermissionGuard permission="rentals:view" />,
    children: [{ index: true, element: <RentalOverduePage /> }],
  },
  {
    path: 'rental-reports',
    element: <PermissionGuard permission="rentals:view" />,
    children: [{ index: true, element: <RentalReportsPage /> }],
  },
];

export const moduleRoutes: RouteObject[] = MODULES.map((module) => {
  const Page = MODULE_PAGES[module.key] ?? ModulePlaceholder;

  return {
    path: `${module.path.replace(/^\//, '')}/*`,
    element: (
      <ModuleGuard moduleKey={module.key}>
        <Page />
      </ModuleGuard>
    ),
  };
});

/** Module keys that currently render a real page — used by the placeholder copy. */
export const IMPLEMENTED_MODULES = Object.keys(MODULE_PAGES) as ModuleKey[];
